import { IconProps } from '@tamagui/helpers-icon';
import { Row } from '@unistocks/components/layout/flex';
import { ActiveTabIndicator } from '@unistocks/components/tabs/indicator';
import { Heading } from '@unistocks/components/text/text';
import { DISABLED_OPACITY } from '@unistocks/shared/consts';
import { withFeedback } from '@unistocks/shared/utils/haptics/with-feedback';
import React, { ReactElement, useState } from 'react';
import {
  AnimatePresence,
  HeadingProps,
  Tabs as TMTabs,
  TabsProps as TMTabsProps,
  TabLayout,
  TabsListProps,
  TabsTabProps,
  YStack,
  styled,
  useControllableState
} from 'tamagui';

export type Tab = {
  /**
   * Label of the tab. If provided, icon will be ignored
   */
  label?: string;
  /**
   * Icon of the tab. If provided, label will be ignored. Icon will be automatically sized and colored based on the active state
   */
  icon?: ReactElement<IconProps>;
  value: string;
  disabled?: boolean;
  color?: HeadingProps['color'];
};

export type TabProps = {
  tab: string;
};

export type RenderTab = (props: Tab['value']) => React.ReactNode;

export type TabsProps = TMTabsProps & {
  tabs: Tab[];
  hideSingleTab?: boolean;
  renderTab: RenderTab;
  /**
   * Props for the tabs container
   */
  tabListProps?: TabsListProps;
  /**
   * Props for each tab component
   */
  tabProps?: TabsTabProps;
};

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  hideSingleTab = true,
  value,
  onValueChange,
  renderTab,
  tabListProps,
  tabProps,
  ...props
}) => {
  const [tabState, setTabState] = useState<{
    /**
     * Layout of the Tab user might intend to select (hovering / focusing)
     */
    intentAt: TabLayout | null;
    /**
     * Layout of the Tab user selected
     */
    activeAt: TabLayout | null;
  }>({
    activeAt: null,
    intentAt: null
  });

  const [activeTab, setActiveTab] = useControllableState({
    defaultProp: tabs[0].value,
    prop: value,
    strategy: 'prop-wins',
    onChange: onValueChange
  });

  const setIntentIndicator = (intentAt: TabLayout | null) => setTabState({ ...tabState, intentAt });
  const setActiveIndicator = (activeAt: TabLayout | null) => setTabState({ ...tabState, activeAt });
  const { activeAt, intentAt } = tabState;

  // Track previous tab in state so direction is derived correctly for both
  // user clicks and external value changes (ref reads during render break React Compiler).
  const [prevActiveTab, setPrevActiveTab] = useState(activeTab);
  const [direction, setDirection] = useState(0);
  if (prevActiveTab !== activeTab) {
    const prevIndex = tabs.findIndex(t => t.value === prevActiveTab);
    const nextIndex = tabs.findIndex(t => t.value === activeTab);
    let newDirection = 0;
    if (prevIndex !== -1 && nextIndex !== -1) {
      if (nextIndex > prevIndex) newDirection = 1;
      else if (nextIndex < prevIndex) newDirection = -1;
    }
    setPrevActiveTab(activeTab);
    setDirection(newDirection);
  }

  const handleOnInteraction: TabsTabProps['onInteraction'] = (type, layout) => {
    if (type === 'select') {
      setActiveIndicator(layout);
    } else {
      setIntentIndicator(layout);
    }
  };

  const handleTabChange = (newVal: string) => {
    setActiveTab(newVal);
  };

  if (hideSingleTab && tabs.length === 1) return renderTab(tabs[0].value);

  return (
    <TMTabs value={activeTab} onValueChange={handleTabChange} {...props}>
      <YStack position="relative">
        <AnimatePresence>
          {intentAt && (
            <ActiveTabIndicator
              key={'intent'}
              hover
              width={intentAt.width * 0.8}
              x={intentAt.x + intentAt.width * 0.1}
            />
          )}
        </AnimatePresence>
        <AnimatePresence>
          {activeAt && (
            <ActiveTabIndicator
              key={'active'}
              active
              width={activeAt.width * 0.8}
              x={activeAt.x + activeAt.width * 0.1}
            />
          )}
        </AnimatePresence>

        <TMTabs.List
          loop={false}
          aria-label="Tab bar"
          borderBottomLeftRadius={0}
          borderBottomRightRadius={0}
          paddingBottom="$1.5"
          borderColor="$color3"
          borderBottomWidth="$0.5"
          backgroundColor="transparent"
          {...tabListProps}
        >
          {tabs.map(({ label, icon, value: tab, disabled, color }, index) => {
            const iconOnly = icon && !label;
            const first = index === 0;

            return (
              <TMTabs.Tab
                key={tab}
                unstyled
                tabIndex={0}
                value={tab}
                flex={1}
                minW={iconOnly ? '$10' : undefined}
                ml={first ? undefined : '$3'}
                alignItems="center"
                justifyContent="center"
                py={'$2.5'}
                cursor="pointer"
                bblr={0}
                bbrr={0}
                onInteraction={handleOnInteraction}
                onPressIn={withFeedback}
                disabled={disabled}
                disabledStyle={{ opacity: DISABLED_OPACITY }}
                $sm={{
                  maxW: iconOnly ? '$10' : undefined
                }}
                {...tabProps}
              >
                {label && icon ? (
                  <Row alignItems={'center'}>
                    <Heading
                      active={tab === activeTab}
                      size={'$6'}
                      fw={'$4'}
                      color={color}
                      flexGrow={0}
                      flexShrink={0}
                    >
                      {label}
                    </Heading>
                    {icon
                      ? React.cloneElement(icon, {
                          size: '$1',
                          color: (color as any) ?? (tab === activeTab ? '$unistocks' : '$gray11')
                        })
                      : null}
                  </Row>
                ) : label ? (
                  <Heading active={tab === activeTab} size={'$6'} fw={'$4'} color={color}>
                    {label}
                  </Heading>
                ) : icon ? (
                  React.cloneElement(icon, {
                    size: '$2.5',
                    color: (color as any) ?? (tab === activeTab ? '$unistocks' : '$gray11')
                  })
                ) : null}
              </TMTabs.Tab>
            );
          })}
        </TMTabs.List>

        <AnimatePresence mode="wait" custom={{ direction }} initial={false}>
          <AnimatedYStack key={activeTab} slideDirection={direction}>
            <TMTabs.Content value={activeTab} forceMount tabIndex={-1} mt={'$2.5'}>
              {renderTab(activeTab)}
            </TMTabs.Content>
          </AnimatedYStack>
        </AnimatePresence>
      </YStack>
    </TMTabs>
  );
};

Tabs.displayName = 'Tabs';

const AnimatedYStack = styled(YStack, {
  transition: '200ms',
  variants: {
    // 1 = moved right (forward), 0 = nowhere, -1 = moved left (back)
    slideDirection: {
      ':number': direction => ({
        enterStyle: {
          // moving forward: enter from right (+50)
          // moving back: enter from left (-50)
          x: direction > 0 ? 50 : -50,
          opacity: 0
        },
        exitStyle: {
          zIndex: 0,
          // moving forward: exit to left (-50)
          // moving back: exit to right (+50)
          x: direction > 0 ? -50 : 50,
          opacity: 0
        }
      })
    }
  } as const
});
